import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'wouter';
import { Button } from '~/modules/Elements/AKUI/Button';
import Box from '~/modules/Elements/AKUI/Primitives/Box';
import Typography from '~/modules/Elements/AKUI/Primitives/Typography';
import { useBackground } from '~/modules/Elements/BackgroundContext';
import Logo from '~/modules/Elements/Logo';
import SmoothLink from '~/modules/Elements/SmoothLink';
import MultiplayerManager from '~/modules/Multiplayer/MultiplayerManager';
import { twx } from '~/utils/twx';

function MultiplayerLanding() {
  const [, setLocation] = useLocation();
  useBackground(true);

  const [mode, setMode] = useState<'menu' | 'create' | 'join'>('menu');
  const [playerName, setPlayerName] = useState(() => {
    try {
      return localStorage.getItem('karaoke-party-name') || '';
    } catch {
      return '';
    }
  });
  const [roomCode, setRoomCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Check URL for ?join=CODE
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const joinCode = params.get('join');
    if (joinCode) {
      setRoomCode(joinCode.toUpperCase());
      setMode('join');
    }
  }, []);

  // Save name to localStorage
  const saveName = (name: string) => {
    try {
      localStorage.setItem('karaoke-party-name', name);
    } catch {}
  };

  const handleCreate = async () => {
    if (!playerName.trim()) {
      setError('Wpisz swoje imię');
      return;
    }
    setLoading(true);
    setError('');
    saveName(playerName.trim());
    try {
      const code = await MultiplayerManager.createRoom(playerName.trim());
      setLocation(`room/${code}`);
    } catch (e: any) {
      setError(e.message || 'Nie udało się stworzyć pokoju');
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!playerName.trim()) {
      setError('Wpisz swoje imię');
      return;
    }
    if (!roomCode.trim() || roomCode.trim().length < 4) {
      setError('Wpisz kod pokoju');
      return;
    }
    setLoading(true);
    setError('');
    saveName(playerName.trim());
    try {
      await MultiplayerManager.joinRoom(roomCode.trim().toUpperCase(), playerName.trim());
      setLocation(`room/${roomCode.trim().toUpperCase()}`);
    } catch (e: any) {
      setError(e.message || 'Nie udało się dołączyć');
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Karaoke Party - Multiplayer Karaoke</title>
      </Helmet>
      <div className="mx-auto flex min-h-screen w-full max-w-2xl flex-col items-center justify-center gap-6 p-4">
        <div className="flex flex-col items-center gap-2">
          <div className="flex justify-center text-6xl">
            <Logo />
          </div>
          <Typography className="bg-gradient-to-r from-yellow-300 via-pink-400 to-purple-400 bg-clip-text text-3xl font-extrabold tracking-widest text-transparent uppercase">
            Karaoke Party
          </Typography>
        </div>

        {mode === 'menu' && (
          <Box className="w-full gap-5 bg-black/70 p-8 backdrop-blur-sm">
            <Typography className="text-center text-lg text-white/80">
              Graj z przyjaciółmi online - do 16 graczy!
            </Typography>

            <CreateButton onClick={() => setMode('create')}>Stwórz pokój</CreateButton>

            <JoinButton onClick={() => setMode('join')}>Dołącz do pokoju</JoinButton>

            <hr className="my-1 w-full border-white/15" />

            <SmoothLink to="menu/">
              <SmallButton>Gra lokalna (klasyczna)</SmallButton>
            </SmoothLink>
          </Box>
        )}

        {mode === 'create' && (
          <Box className="w-full gap-4 bg-black/70 p-8 backdrop-blur-sm">
            <Typography className="mb-1 text-xl font-semibold">Stwórz nowy pokój</Typography>

            <div className="flex flex-col gap-1">
              <label className="text-sm text-white/50">Twoje imię</label>
              <NameInput
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Wpisz swoje imię..."
                maxLength={20}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>

            {error && <Typography className="rounded bg-red-500/20 px-3 py-2 text-sm text-red-300">{error}</Typography>}

            <CreateButton onClick={handleCreate} disabled={loading}>
              {loading ? 'Tworzenie...' : 'Stwórz pokój'}
            </CreateButton>

            <BackButton
              onClick={() => {
                setMode('menu');
                setError('');
              }}>
              Wróć
            </BackButton>
          </Box>
        )}

        {mode === 'join' && (
          <Box className="w-full gap-4 bg-black/70 p-8 backdrop-blur-sm">
            <Typography className="mb-1 text-xl font-semibold">Dołącz do pokoju</Typography>

            <div className="flex flex-col gap-1">
              <label className="text-sm text-white/50">Twoje imię</label>
              <NameInput
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Wpisz swoje imię..."
                maxLength={20}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && document.getElementById('room-code-input')?.focus()}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm text-white/50">Kod pokoju</label>
              <CodeInput
                id="room-code-input"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                maxLength={6}
                onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
              />
            </div>

            {error && <Typography className="rounded bg-red-500/20 px-3 py-2 text-sm text-red-300">{error}</Typography>}

            <JoinButton onClick={handleJoin} disabled={loading}>
              {loading ? 'Dołączanie...' : 'Dołącz'}
            </JoinButton>

            <BackButton
              onClick={() => {
                setMode('menu');
                setError('');
              }}>
              Wróć
            </BackButton>
          </Box>
        )}

        <Typography className="text-center text-sm text-white/40">
          Otwórz tę stronę na każdym urządzeniu aby grać razem
        </Typography>
      </div>
    </>
  );
}

const CreateButton = twx(
  Button,
)`h-16 w-full bg-gradient-to-r from-green-700/70 to-green-600/70 text-xl transition-all hover:from-green-600/80 hover:to-green-500/80`;
const JoinButton = twx(
  Button,
)`h-16 w-full bg-gradient-to-r from-blue-700/70 to-blue-600/70 text-xl transition-all hover:from-blue-600/80 hover:to-blue-500/80`;
const SmallButton = twx(Button)`text-md h-12 w-full bg-white/10 transition-colors hover:bg-white/20`;
const BackButton = twx(
  Button,
)`text-md h-10 w-full bg-white/5 text-white/60 transition-colors hover:bg-white/10 hover:text-white/80`;

const NameInput = twx.input`w-full rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-lg text-white placeholder-white/30 transition-colors outline-none focus:border-white/50 focus:bg-white/10`;
const CodeInput = twx.input`w-full rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-center font-mono text-2xl tracking-[0.3em] text-white uppercase placeholder-white/30 transition-colors outline-none focus:border-white/50 focus:bg-white/10`;

export default MultiplayerLanding;
